import React from 'react'
import { useHistory } from 'react-router'
import styled from '@emotion/styled'
import { IonIcon } from '@ionic/react'
import { bindActionCreators } from 'redux'
import { useDispatch } from 'react-redux'
import {
  arrowForwardOutline,
  bookOutline,
  checkmarkCircleOutline,
  timeOutline,
} from 'ionicons/icons'
import { toast } from 'react-toastify'
import { authActions } from '../auth/AuthStore'
import { AUTH_COLORS } from '../auth/AuthForm'
import { CursoUsuarioResumo } from '../common/axios'
import { STATUS_MATRICULA } from '../models/MatriculaModel'

type StatusKind = 'pending' | 'finalized' | 'active'

const STATUS_BADGE_VIEW = {
  pending: {
    label: 'Aguardando Pagamento',
    icon: timeOutline,
    background: 'rgba(234, 67, 53, 0.12)',
    foreground: 'rgba(234, 67, 53, 1)',
  },
  active: {
    label: 'Matriculado',
    icon: bookOutline,
    background: 'rgba(255, 100, 34, 0.11)',
    foreground: 'rgba(255, 100, 34, 1)',
  },
  finalized: {
    label: 'Finalizado',
    icon: checkmarkCircleOutline,
    background: 'rgba(52, 168, 83, 0.14)',
    foreground: 'rgba(52, 168, 83, 1)',
  },
}

export const CursoCard = ({ curso }: { curso: CursoUsuarioResumo }) => {
  const {
    Container,
    ImageWrap,
    Img,
    CategoryPill,
    Content,
    Title,
    ProgressBlock,
    ProgressInfo,
    ProgressTrack,
    ProgressFill,
    Actions,
    AccessButton,
    CertificateButton,
  } = CursoCard
  const history = useHistory()
  const dispatch = useDispatch()
  const actions = bindActionCreators(authActions, dispatch)
  const isCompleted = curso.statusDetran === STATUS_MATRICULA.FINALIZADO
  const progressPercent = curso.modulos?.progressoPercentual ?? 0

  function shouldShowCertificateButton() {
    return isCompleted && curso.permiteEmissaoCertificado !== false
  }

  const handleCursos = async () => {
    actions.newTokenMatricula({
      matriculaId: String(curso.matriculaId),
      callback: () => history.replace(`/curso/${curso.id}/home`),
    })
  }

  const handleCardKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    const target = event.target as HTMLElement
    if (target.closest('button, a')) return
    if (event.key !== 'Enter' && event.key !== ' ') return

    event.preventDefault()
    handleCursos()
  }

  const handleClickCertificado = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()

    if (!shouldShowCertificateButton() || !curso.identificacaoCertificado) {
      toast.error('Não foi possível abrir o certificado')
      return
    }

    if (process.env.NODE_ENV === 'production') {
      window.open(`https://app.newdriver.com.br/certificado/${curso.identificacaoCertificado}`)
      return
    }

    window.open(`http://localhost:3001/certificado/${curso?.identificacaoCertificado}`)
  }

  const urlImg = process.env.PUBLIC_URL + '/assets/cursos/' + curso.id + '.webp'

  return (
    <Container
      id="MenuCurso"
      role="button"
      tabIndex={0}
      onClick={handleCursos}
      onKeyDown={handleCardKeyDown}
    >
      <ImageWrap>
        <Img src={urlImg} alt="" />
        <CategoryPill>{curso?.categorias || 'Curso de especialização'}</CategoryPill>
      </ImageWrap>
      <Content>
        <StatusBadge curso={curso} />
        <Title>{curso?.nome}</Title>
        <ProgressBlock>
          <ProgressInfo>
            <span>Módulos:</span>
            <strong>{`${curso.modulos?.concluidos ?? 0} de ${curso.modulos?.total ?? 0}`}</strong>
          </ProgressInfo>
          <ProgressTrack>
            <ProgressFill style={{ width: `${progressPercent}%` }} />
          </ProgressTrack>
        </ProgressBlock>
        <Actions>
          <AccessButton>
            {isCompleted ? 'REVISAR CONTEÚDO' : 'ACESSAR'} <IonIcon icon={arrowForwardOutline} />
          </AccessButton>
          {shouldShowCertificateButton() ? (
            <CertificateButton type="button" onClick={handleClickCertificado}>
              VER CERTIFICADO
            </CertificateButton>
          ) : null}
        </Actions>
      </Content>
    </Container>
  )
}

CursoCard.Container = styled.article`
  width: 100%;
  min-height: 440px;
  border: 1px solid ${AUTH_COLORS.blue};
  border-radius: 8px;
  padding: 0;
  background: #ffffff;
  overflow: hidden;
  font-family: Rubik, sans-serif;
  text-align: left;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;

  :hover {
    border-color: ${AUTH_COLORS.orange};
    box-shadow: 0 10px 30px rgba(18, 42, 65, 0.12);
    transform: translateY(-1px);
  }

  :focus-visible {
    border-color: ${AUTH_COLORS.orange};
    outline: 2px solid rgba(255, 100, 34, 0.24);
    outline-offset: 3px;
  }
`

CursoCard.ImageWrap = styled.div`
  position: relative;
  width: 100%;
  height: 158px;
  background: #d9d9d9;
`

CursoCard.Img = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`

CursoCard.CategoryPill = styled.span`
  position: absolute;
  left: 20px;
  top: 20px;
  max-width: calc(100% - 40px);
  border-radius: 4px;
  background: #ffffff;
  color: ${AUTH_COLORS.blue};
  font-size: 12px;
  line-height: 12px;
  padding: 6px 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-shadow: 0 1px 4px rgba(25, 33, 61, 0.08);
`

CursoCard.Content = styled.div`
  flex: 1;
  padding: 24px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

CursoCard.Title = styled.strong`
  min-height: 72px;
  color: ${AUTH_COLORS.blue};
  font-size: 22px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0;
`

CursoCard.ProgressBlock = styled.div``

CursoCard.ProgressInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: ${AUTH_COLORS.blue};
  font-size: 13px;
  line-height: 18px;

  span {
    font-weight: 400;
  }

  strong {
    font-weight: 500;
  }
`

CursoCard.ProgressTrack = styled.div`
  width: 100%;
  height: 5px;
  margin-top: 6px;
  border-radius: 999px;
  background: rgba(209, 215, 224, 1);
  overflow: hidden;
`

CursoCard.ProgressFill = styled.div`
  width: 0;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #ffd733 0%, #ff6422 100%);
  transition: width 0.2s ease;
`

CursoCard.Actions = styled.div`
  margin-top: 8px;
`

CursoCard.AccessButton = styled.span`
  width: 100%;
  height: 40px;
  border-radius: 6px;
  background: ${AUTH_COLORS.orange};
  color: #ffffff;
  font-family: Rubik, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  ion-icon {
    width: 14px;
    height: 14px;
  }
`

CursoCard.CertificateButton = styled.button`
  width: 100%;
  margin-top: 14px;
  border: 0;
  padding: 0;
  background: transparent;
  color: ${AUTH_COLORS.blue};
  font-family: Rubik, sans-serif;
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
  text-decoration: underline;
  cursor: pointer;

  :hover {
    color: ${AUTH_COLORS.orange};
  }
`

function StatusBadge({ curso }: { curso: CursoUsuarioResumo }) {
  const statusKind = getStatusKind(curso)
  const statusView = STATUS_BADGE_VIEW[statusKind]

  return (
    <StatusBadge.Container statusKind={statusKind}>
      <IonIcon icon={statusView.icon} /> {statusView.label}
    </StatusBadge.Container>
  )
}

StatusBadge.Container = styled.div<{ statusKind: StatusKind }>((props) => {
  const statusView = STATUS_BADGE_VIEW[props.statusKind]

  return `
  width: fit-content;
  max-width: 100%;
  min-height: 26px;
  border: 1px solid ${statusView.foreground};
  border-radius: 999px;
  background: ${statusView.background};
  color: ${statusView.foreground};
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  line-height: 1;
  letter-spacing: 0;
  font-weight: 600;
  padding: 5px 10px;
  text-transform: uppercase;

  ion-icon {
    flex: 0 0 auto;
    width: 15px;
    height: 15px;
    color: ${statusView.foreground};
    --ionicon-stroke-width: 55px;
  }
`
})

function getStatusKind(curso: CursoUsuarioResumo): StatusKind {
  if (curso.statusDetran === STATUS_MATRICULA.FINALIZADO) return 'finalized'
  if (!curso.dataPagamento && !curso.isMatriculaParceiro) return 'pending'
  return 'active'
}
